import React, { useCallback } from "react";
import { Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useGetLightPartnerOffering } from "@/data/api";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { COMING_FROM_CATALOG } from "@/offering_v2/hooks/use_handle_press_back";
import "./category_offering_card.css";

interface CategoryOfferingCardProps {
  image: string;
  title: string;
  description: string;
  longDescription?: string;
  offeringSlug: string;
  catalogSlug?: string;
  comingSoon: boolean;
  membership?: boolean;
  fromCategory?: string;
  ctaLabel?: string;
}

export function CategoryOfferingCard(props: CategoryOfferingCardProps) {
  const {
    title,
    description,
    longDescription,
    offeringSlug,
    catalogSlug,
    comingSoon,
    membership,
    fromCategory,
    image,
    ctaLabel,
  } = props;

  const { fbt } = useI18n();
  const analytics = useAnalytics();
  const partnerOffering = useGetLightPartnerOffering(offeringSlug);

  const searchParams = new URLSearchParams(window.location.search);

  if (fromCategory) {
    searchParams.set("fromCategory", fromCategory);
  }

  if (catalogSlug) {
    searchParams.set("fromCatalog", catalogSlug);
  }

  const handleClick = useCallback(() => {
    analytics.event(
      `Category Offering Card Click`,
      {
        Template: partnerOffering.template,
        Title: title,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, title, partnerOffering.template]);

  const cardComponent = (
    <Card className="category-offering-card h-100">
      {!longDescription && (
        <Card.Img
          variant="top"
          src={image}
          title={title}
          style={{
            objectFit: "cover",
            width: "100%",
            height: 193,
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
          }}
        />
      )}
      <Card.Body className="d-flex flex-column">
        <Card.Title className="fw-bold fs-4 mb-1">{title}</Card.Title>
        <Card.Text className="text-muted mb-4">{description}</Card.Text>
        <div
          className="long-description"
          dangerouslySetInnerHTML={{ __html: longDescription || "" }}
        />
        <div className="mt-1">
          <Button
            className="w-100"
            variant={longDescription ? "primary" : "outline-primary"}
            disabled={comingSoon}
          >
            {ctaLabel ||
              (comingSoon
                ? fbt("Coming Soon")
                : membership
                ? fbt("Subscribe now")
                : fbt("Book now"))}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  if (comingSoon) {
    return cardComponent;
  }

  return (
    <Link
      to={{
        pathname: `/offerings/${offeringSlug}`,
        search: searchParams.toString(),
        state: { comingFrom: COMING_FROM_CATALOG },
      }}
      title={title}
      style={{ textDecoration: "none" }}
      onClick={handleClick}
    >
      {cardComponent}
    </Link>
  );
}
